import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';
import {
	HubConnection,
	HubConnectionBuilder,
	LogLevel,
} from '@microsoft/signalr';
import AuthContext, { isTokenExpired } from './authContext';
import showNotification from '../components/extras/showNotification';
import getSignalRHubUrl from '../utils/signalRHubUrl';

export interface BoRealtimeContextValue {
	connection: HubConnection | null;
	isConnected: boolean;
	/** Subscribe to a server-invoked client method; returns unsubscribe. */
	registerClientMethod: (
		methodName: string,
		handler: (...args: unknown[]) => void,
	) => () => void;
}

const BoRealtimeContext = createContext<BoRealtimeContextValue | undefined>(undefined);

export const BoRealtimeProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
	const { authData } = useContext(AuthContext);
	const [connection, setConnection] = useState<HubConnection | null>(null);
	const [isConnected, setIsConnected] = useState(false);

	useEffect(() => {
		const token = authData?.token?.accessToken;
		const expiry = authData?.token?.accessTokenExpiry;
		const expired = !token || (expiry ? isTokenExpired(expiry) : true);

		if (expired) {
			setConnection((prev) => {
				if (prev) {
					prev.stop();
				}
				return null;
			});
			setIsConnected(false);
			return undefined;
		}

		const url = getSignalRHubUrl();
		const conn = new HubConnectionBuilder()
			.withUrl(url, {
				accessTokenFactory: () => token,
			})
			.withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
			.configureLogging(LogLevel.Warning)
			.build();

		const onReceiveBroadcast = (payload: unknown) => {
			if (payload && typeof payload === 'object' && payload !== null && 'message' in payload) {
				const p = payload as { title?: string; message?: unknown; type?: string };
				showNotification(
					p.title ?? 'Notification',
					String(p.message),
					p.type ?? 'info',
				);
			} else if (typeof payload === 'string') {
				showNotification('Notification', payload, 'info');
			} else {
				showNotification('Notification', JSON.stringify(payload), 'info');
			}
		};

		conn.on('ReceiveBroadcast', onReceiveBroadcast);
		conn.onreconnecting(() => setIsConnected(false));
		conn.onreconnected(() => setIsConnected(true));
		conn.onclose(() => setIsConnected(false));

		setConnection(conn);

		let cancelled = false;
		conn
			.start()
			.then(() => {
				if (!cancelled) setIsConnected(true);
			})
			.catch(() => {
				if (!cancelled) setIsConnected(false);
			});

		return () => {
			cancelled = true;
			conn.off('ReceiveBroadcast', onReceiveBroadcast);
			conn.stop();
			setIsConnected(false);
			setConnection(null);
		};
	}, [authData?.token?.accessToken, authData?.token?.accessTokenExpiry]);

	const registerClientMethod = useCallback(
		(methodName: string, handler: (...args: unknown[]) => void) => {
			if (!connection) return () => {};
			connection.on(methodName, handler);
			return () => {
				connection.off(methodName, handler);
			};
		},
		[connection],
	);

	const value = useMemo<BoRealtimeContextValue>(
		() => ({
			connection,
			isConnected,
			registerClientMethod,
		}),
		[connection, isConnected, registerClientMethod],
	);

	return <BoRealtimeContext.Provider value={value}>{children}</BoRealtimeContext.Provider>;
};

export function useBoRealtime(): BoRealtimeContextValue {
	const ctx = useContext(BoRealtimeContext);
	if (!ctx) {
		throw new Error('useBoRealtime must be used within BoRealtimeProvider');
	}
	return ctx;
}
